import { type ReactElement } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { TableHead } from '@/components/ui/table';
import { cn } from '@/lib/utils';

/** Re-export for consumers that use the generic table head */
export type { SortOrder };

interface SortableTableHeadProps<T extends string> {
  children: React.ReactNode;
  field: T;
  currentSortBy: T;
  currentSortOrder: SortOrder;
  onSort: (field: T) => void;
  className?: string;
  /** Aligns the header content (label + sort arrows) within the cell. Only applied to the inner flex container. */
  alignContent?: 'start' | 'center';
}

/**
 * Generic sortable table header component with up/down arrows.
 * Works with any string-based sort field type.
 */
export function SortableTableHead<T extends string>({
  children,
  field,
  currentSortBy,
  currentSortOrder,
  onSort,
  className,
  alignContent = 'start',
}: SortableTableHeadProps<T>): ReactElement {
  const isActive = currentSortBy === field;
  const innerAlignClass =
    alignContent === 'center' ? 'justify-center' : 'justify-start';

  return (
    <TableHead
      className={cn('cursor-pointer select-none hover:bg-muted/50', className)}
      onClick={() => onSort(field)}
    >
      <div className={cn('flex items-center gap-1', innerAlignClass)}>
        {children}
        <span className="inline-flex flex-col">
          <ArrowUp
            className={cn(
              'h-3 w-3 -mb-1',
              isActive && currentSortOrder === SortOrder.Asc
                ? 'text-foreground'
                : 'text-muted-foreground/40'
            )}
          />
          <ArrowDown
            className={cn(
              'h-3 w-3',
              isActive && currentSortOrder === SortOrder.Desc
                ? 'text-foreground'
                : 'text-muted-foreground/40'
            )}
          />
        </span>
      </div>
    </TableHead>
  );
}
